// QUIÉN HA LEÍDO QUÉ — QUE NO ES LO MISMO QUE «ESTÁ LEÍDO».
//
// Gemelo de app/src/lib/lecturas.ts, vigilado por
// `scripts/guard-gemelos-catalogo.mjs`: son dos copias y no una compartida
// porque los dos repos no comparten paquete (`admin/` es Vite, `app/` es Expo).
// Las dos importan el cliente de `../services/supabase`, así que el cuerpo es
// idéntico byte a byte y el guard lo exige.
//
// POR QUÉ EXISTE
// --------------
// `messages.read` era UN booleano por mensaje, así que el primero que abría un
// hilo lo apagaba para todos. En el buzón del club lo comparten siete personas
// (un admin y seis directores): un director entraba, y los otros cinco veían
// «leído» algo que no habían abierto. Medido en producción: 21 de 21 mensajes
// del buzón estaban marcados como leídos.
//
// La mig 324 separa las dos preguntas, y la frase que hay que llevarse es ésta:
//
//   · `message_reads`  -> QUIÉN lo ha leído. Una fila por persona y mensaje.
//   · `messages.read`  -> SU DESTINATARIO lo ha leído. Lo deriva un trigger de
//     la 324 cuando el que registra la lectura es el destinatario, y solo
//     entonces. Sigue siendo la respuesta correcta a «¿lo ha visto ya ese
//     alumno?», que es lo que preguntan los paneles de tareas.
//
// Así que esto NO sustituye a `messages.read` en todas partes: lo sustituye
// donde la pantalla afirma «no lo has leído TÚ».
import { supabase } from '../services/supabase';

/**
 * Cuántos ids van en cada petición.
 *
 * Los `in.(...)` viajan en la URL, y un uuid son 37 caracteres: 200 ids son
 * 7,4 KB de línea de petición, por encima de lo que varios proxys aceptan sin
 * devolver un 414 que nadie espera. Se trocea aquí, una vez, en lugar de
 * dejarlo a lo que cada pantalla cargue.
 */
const POR_PETICION = 50;

/**
 * Cuántos ids se traen como máximo para restarlos a mano.
 *
 * ⚠️ Esto NO es un adorno. Contar «lo que no tengo registrado yo» obliga a
 * traerse los ids y restar, y una página de PostgREST tiene techo (`max-rows`,
 * 1000 por defecto en Supabase). Pasado el techo la lista vuelve CORTADA, sin
 * error: el recuento saldría de menos y nadie lo notaría, que es justo la clase
 * de fallo que esta serie viene a quitar. Así que quien cuenta compara con este
 * tope y, si lo toca, devuelve «no se sabe» en vez de un número de menos.
 *
 * Hoy el club entero tiene ~100 mensajes, así que esto es un seguro, no un
 * límite operativo. El día que se toque, la salida es una RPC que lo cuente en
 * la base — no subir el número.
 */
export const TOPE_IDS = 1000;

function trozos<T>(lista: T[], tamano: number): T[][] {
  const salida: T[][] = [];
  for (let i = 0; i < lista.length; i += tamano) salida.push(lista.slice(i, i + tamano));
  return salida;
}

/**
 * De estos mensajes, cuáles he leído YO.
 *
 * ⚠️ El `.eq('user_id', miId)` NO es decorativo, y quitarlo no daría error: la
 * policy de SELECT de `message_reads` es `user_id = auth.uid() OR is_admin()`,
 * así que a un admin —y a un director, que `is_admin()` incluye— le devolvería
 * las lecturas de TODO EL MUNDO. El buzón volvería a marcar como leído lo que
 * abrió otro, que es exactamente el fallo que la 324 viene a quitar, y volvería
 * en silencio: con datos, sin error y con la cara de estar funcionando.
 *
 * El error se devuelve, no se traga: un conjunto a medias diría «esto no lo has
 * leído» de cosas que sí, y quien pinta necesita poder decir «no se sabe».
 */
export async function misLecturas(
  ids: string[],
  miId: string
): Promise<{ leidos: Set<string>; error: string | null }> {
  const leidos = new Set<string>();
  if (ids.length === 0) return { leidos, error: null };
  for (const trozo of trozos(ids, POR_PETICION)) {
    const { data, error } = await supabase
      .from('message_reads')
      .select('message_id')
      .eq('user_id', miId)
      .in('message_id', trozo);
    if (error) return { leidos: new Set<string>(), error: error.message || 'Error desconocido' };
    for (const fila of (data ?? []) as { message_id: string }[]) leidos.add(fila.message_id);
  }
  return { leidos, error: null };
}

/**
 * Dejar constancia de que YO he leído estos mensajes.
 *
 * Antes esto era un `update({ read: true })` con un filtro, y por eso no hacía
 * falta saber los ids. Ahora hace falta: una lectura es una fila con nombre.
 *
 * Se consulta primero lo que ya está registrado porque la tabla no acepta
 * `ON CONFLICT` desde el cliente —`authenticated` solo tiene SELECT e INSERT, y
 * un upsert pediría UPDATE—, así que una sola fila repetida tumbaría el lote
 * entero con un 23505. Si la consulta previa falla, NO se inserta a ciegas y se
 * devuelve el error: insertar el lote completo sería jugársela a que no hubiera
 * ni una repetida.
 *
 * Y si el INSERT falla, el error sube. Nadie avisa al usuario al abrir un chat
 * —un aviso en cada apertura es ruido—, pero el globo NO se apaga en local: así
 * reaparece en la siguiente carga, que es la verdad. La propia repetición lo
 * cura: a la siguiente, lo que ya esté registrado queda fuera del lote.
 */
export async function marcarLeidos(
  ids: string[],
  miId: string
): Promise<{ error: string | null }> {
  if (ids.length === 0) return { error: null };
  const { leidos, error: errorLectura } = await misLecturas(ids, miId);
  if (errorLectura) return { error: errorLectura };
  const faltan = Array.from(new Set(ids)).filter((id) => !leidos.has(id));
  if (faltan.length === 0) return { error: null };
  for (const trozo of trozos(faltan, POR_PETICION)) {
    const { error } = await supabase
      .from('message_reads')
      .insert(trozo.map((id) => ({ message_id: id, user_id: miId })));
    if (error) return { error: error.message || 'Error desconocido' };
  }
  return { error: null };
}
